import { ModuleEvent, EventHandler } from '../types';

class EventBus {
  private listeners: Map<string, EventHandler[]> = new Map();
  private middlewares: ((event: ModuleEvent) => ModuleEvent | null)[] = [];

  // Subscribe to events
  on<T = unknown>(eventType: string, handler: EventHandler<T>): () => void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, []);
    }
    
    this.listeners.get(eventType)!.push(handler as EventHandler);
    
    // Return unsubscribe function
    return () => {
      const handlers = this.listeners.get(eventType);
      if (handlers) {
        const index = handlers.indexOf(handler as EventHandler);
        if (index > -1) {
          handlers.splice(index, 1);
        }
      }
    };
  }

  // Emit events
  async emit(eventType: string, payload?: unknown, source: string = 'unknown'): Promise<void> {
    let event: ModuleEvent = {
      type: eventType,
      payload,
      timestamp: Date.now(),
      source
    };

    // Apply middlewares
    for (const middleware of this.middlewares) {
      const result = middleware(event);
      if (result === null) {
        // Middleware blocked the event
        return;
      }
      event = result;
    }

    const handlers = this.listeners.get(eventType);
    if (handlers) {
      // Execute all handlers concurrently
      await Promise.allSettled(
        handlers.map(handler => handler(event.payload))
      );
    }
  }

  // Add middleware for event processing
  use(middleware: (event: ModuleEvent) => ModuleEvent | null): void {
    this.middlewares.push(middleware);
  }

  // Remove all listeners for cleanup
  clear(): void {
    this.listeners.clear();
    this.middlewares.length = 0;
  }
}

// Global event bus instance
export const eventBus = new EventBus();
export { EventBus };